import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaCalendarAlt, FaEdit, FaPlus, FaSearch, FaShieldAlt, FaSpinner, FaTrash, FaUser } from 'react-icons/fa';

function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [esAdmin, setEsAdmin] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('shortToken') || localStorage.getItem('shortToken');
    if (!token) {
      throw new Error('No se encontró el token de autenticación');
    }
    return { Authorization: `Bearer ${token}` };
  };

  const clearForm = () => {
    setNombre('');
    setEmail('');
    setPassword('');
    setEsAdmin(false);
    setEditingUser(null);
  };

  useEffect(() => {
    initialize();
  }, []);

  async function initialize() {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await axios.get('/api/usuarios/me', { headers: getAuthHeaders() });
      const user = response.data;
      setCurrentUser(user);

      if (user.es_admin) {
        setIsAdminUser(true);
        await fetchUsuarios();
      } else {
        setIsAdminUser(false);
      }

      await Promise.all([fetchUpcomingAppointments(user.id), fetchPastAppointments(user.id)]);
    } catch (err) {
      setError(`Error al verificar permisos: ${err.response?.data?.error || err.message}`);
      setIsAdminUser(false);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUsuarios() {
    try {
      const response = await axios.get('/api/usuarios', { headers: getAuthHeaders() });
      setUsuarios(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(`Error al cargar usuarios: ${err.response?.data?.error || err.message}`);
    }
  }

  async function fetchUpcomingAppointments(userId) {
    try {
      const response = await axios.get(`/api/usuarios/${userId}/citas/upcoming`, { headers: getAuthHeaders() });
      setUpcomingAppointments(Array.isArray(response.data) ? response.data : []);
    } catch (_err) {
      setUpcomingAppointments([]);
    }
  }

  async function fetchPastAppointments(userId) {
    try {
      const response = await axios.get(`/api/usuarios/${userId}/citas/past`, { headers: getAuthHeaders() });
      setPastAppointments(Array.isArray(response.data) ? response.data : []);
    } catch (_err) {
      setPastAppointments([]);
    }
  }

  const filteredUsuarios = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) {
      return usuarios;
    }
    return usuarios.filter((usuario) =>
      usuario.nombre?.toLowerCase().includes(term) || usuario.email?.toLowerCase().includes(term)
    );
  }, [usuarios, searchTerm]);

  const totalAdmins = useMemo(() => usuarios.filter((u) => u.es_admin).length, [usuarios]);

  const openCreateModal = () => {
    clearForm();
    setIsModalOpen(true);
  };

  const openEditModal = (usuario) => {
    setEditingUser(usuario);
    setNombre(usuario.nombre || '');
    setEmail(usuario.email || '');
    setPassword('');
    setEsAdmin(Boolean(usuario.es_admin));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    clearForm();
  };

  const handleCreateUsuario = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        '/api/usuarios',
        { nombre, email, password, es_admin: esAdmin },
        { headers: getAuthHeaders() }
      );
      setUsuarios((prev) => [...prev, response.data]);
      setSuccess('Usuario creado correctamente.');
      closeModal();
    } catch (err) {
      setError(`Error al crear usuario: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateUsuario = async (e) => {
    e.preventDefault();
    if (!editingUser) {
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const payload = { nombre, es_admin: esAdmin };
      if (password) {
        payload.password = password;
      }

      const response = await axios.put(`/api/usuarios/${editingUser.id}`, payload, {
        headers: getAuthHeaders(),
      });

      setUsuarios((prev) =>
        prev.map((u) => (u.id === editingUser.id ? { ...u, nombre: response.data.nombre, es_admin: response.data.es_admin } : u))
      );
      setSuccess('Usuario actualizado correctamente.');
      closeModal();
    } catch (err) {
      setError(`Error al actualizar usuario: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUsuario = async (id) => {
    if (!window.confirm('¿Seguro que quieres eliminar este usuario?')) {
      return;
    }

    setDeletingId(id);
    setError('');
    setSuccess('');

    try {
      await axios.delete(`/api/usuarios/${id}`, { headers: getAuthHeaders() });
      setUsuarios((prev) => prev.filter((usuario) => usuario.id !== id));
      setSuccess('Usuario eliminado correctamente.');
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.details || err.message;
      setError(`Error al eliminar usuario: ${message}`);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-60 items-center justify-center">
        <FaSpinner className="animate-spin text-3xl text-slate-500" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="mb-6 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <h2 className="text-3xl font-bold text-slate-900">Usuarios</h2>
        <p className="mt-1 text-sm text-slate-500">
          Gestiona miembros, permisos y disponibilidad desde un solo panel.
        </p>
      </div>

      {error ? (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}
      {success ? (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</div>
      ) : null}

      {isAdminUser ? (
        <>
          <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
              <p className="text-xs uppercase tracking-wide text-slate-500">Total usuarios</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{usuarios.length}</p>
            </div>
            <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
              <p className="text-xs uppercase tracking-wide text-slate-500">Administradores</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{totalAdmins}</p>
            </div>
            <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
              <p className="text-xs uppercase tracking-wide text-slate-500">Resultados filtrados</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{filteredUsuarios.length}</p>
            </div>
          </div>

          <div className="mb-5 flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 md:flex-row md:items-center">
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none transition focus:border-slate-500"
              />
            </div>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-700"
            >
              <FaPlus className="mr-2" />
              Añadir usuario
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {filteredUsuarios.length > 0 ? (
              filteredUsuarios.map((usuario) => (
                <div key={usuario.id} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{usuario.nombre || 'Sin nombre'}</h3>
                      <p className="text-sm text-slate-600">{usuario.email || 'Sin email'}</p>
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        usuario.es_admin ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {usuario.es_admin ? 'Admin' : 'Usuario'}
                    </span>
                  </div>

                  <div className="mb-4 grid grid-cols-1 gap-2 text-xs text-slate-600 md:grid-cols-2">
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      <span className="font-medium">Radicale:</span> {usuario.radicale_username || 'N/A'}
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      <span className="font-medium">Calendar ID:</span> {usuario.calendar_id || 'N/A'}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(usuario)}
                      className="inline-flex items-center rounded-lg bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-200"
                    >
                      <FaEdit className="mr-1" />
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteUsuario(usuario.id)}
                      disabled={deletingId === usuario.id}
                      className="inline-flex items-center rounded-lg bg-red-100 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {deletingId === usuario.id ? <FaSpinner className="mr-1 animate-spin" /> : <FaTrash className="mr-1" />}
                      Eliminar
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-xl bg-white p-5 text-sm text-slate-500 shadow-sm ring-1 ring-slate-200">
                No se encontraron usuarios con ese filtro.
              </div>
            )}
          </div>
        </>
      ) : currentUser ? (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 xl:col-span-1">
            <h3 className="mb-3 text-lg font-semibold text-slate-900">Tu perfil</h3>
            <div className="space-y-2 text-sm text-slate-700">
              <p className="flex items-center">
                <FaUser className="mr-2 text-slate-400" />
                {currentUser.nombre}
              </p>
              <p>{currentUser.email}</p>
              <p className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                <FaShieldAlt className="mr-1" />
                {currentUser.es_admin ? 'Administrador' : 'Usuario'}
              </p>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-slate-50 p-3 text-center">
                <p className="text-xs text-slate-500">Próximas</p>
                <p className="text-xl font-bold text-slate-900">{upcomingAppointments.length}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-3 text-center">
                <p className="text-xs text-slate-500">Pasadas</p>
                <p className="text-xl font-bold text-slate-900">{pastAppointments.length}</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 xl:col-span-1">
            <h4 className="mb-3 text-base font-semibold text-slate-900">Próximas citas</h4>
            {upcomingAppointments.length > 0 ? (
              <div className="space-y-2">
                {upcomingAppointments.map((appointment) => (
                  <div key={appointment.id} className="rounded-lg border border-slate-200 p-3 text-sm text-slate-700">
                    <p className="font-medium">
                      <FaCalendarAlt className="mr-1 inline text-slate-400" />
                      {appointment.fecha}
                    </p>
                    <p>{appointment.hora_inicio} - {appointment.hora_fin}</p>
                    <p className="text-xs text-slate-500">{appointment.servicio?.nombre || 'Sin servicio'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No tienes citas próximas.</p>
            )}
          </div>

          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 xl:col-span-1">
            <h4 className="mb-3 text-base font-semibold text-slate-900">Citas pasadas</h4>
            {pastAppointments.length > 0 ? (
              <div className="space-y-2">
                {pastAppointments.map((appointment) => (
                  <div key={appointment.id} className="rounded-lg border border-slate-200 p-3 text-sm text-slate-700">
                    <p className="font-medium">
                      <FaCalendarAlt className="mr-1 inline text-slate-400" />
                      {appointment.fecha}
                    </p>
                    <p>{appointment.hora_inicio} - {appointment.hora_fin}</p>
                    <p className="text-xs text-slate-500">{appointment.servicio?.nombre || 'Sin servicio'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No hay citas pasadas registradas.</p>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-500">No se pudo cargar la información del usuario.</p>
      )}

      {isModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="mb-4 text-xl font-semibold text-slate-900">
              {editingUser ? 'Editar usuario' : 'Añadir usuario'}
            </h3>
            <form onSubmit={editingUser ? handleUpdateUsuario : handleCreateUsuario} className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Nombre</label>
                <input
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  required
                  disabled={Boolean(editingUser)}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  {editingUser ? 'Nueva contraseña (opcional)' : 'Contraseña'}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  required={!editingUser}
                />
              </div>
              <label className="flex items-center text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={esAdmin}
                  onChange={(e) => setEsAdmin(e.target.checked)}
                  className="mr-2"
                />
                Es administrador
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? <FaSpinner className="mr-2 animate-spin" /> : null}
                  {editingUser ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Usuarios;
